import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlan } from "@/lib/use-plan";
import { Button } from "@/components/ui/button";

interface PlanLockProps {
  feature: string;
  title?: string;
  description?: string;
  children?: React.ReactNode;
}

/**
 * Wraps a route or section. If the workspace's effective plan has the feature,
 * children render; otherwise we render a lock card with an upgrade CTA.
 */
export default function PlanLock({ feature, title, description, children }: PlanLockProps) {
  const { features, loading, effectivePlan } = usePlan();
  if (loading) return null;
  if (features[feature]) return <>{children}</>;

  return (
    <main className="flex-1 px-6 md:px-10 py-16 grid place-items-center" data-testid={`plan-lock-${feature}`}>
      <div className="max-w-md text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-oxblood/30 text-[10px] uppercase tracking-[0.22em] text-oxblood">
          <Lock className="w-3 h-3" /> Pro feature
        </span>
        <h1 className="font-display text-3xl mt-6">{title || "Upgrade to unlock"}</h1>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          {description ||
            "This is part of the Pro toolkit — barcode scanning, outfits, loans, reports, audit log, CSV import, and team collaboration. Your current plan: " +
              effectivePlan +
              "."}
        </p>
        <Button asChild data-testid="plan-lock-cta"
          className="mt-8 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
          <Link to="/app/settings/billing">Choose a plan</Link>
        </Button>
      </div>
    </main>
  );
}
